import { i18nReady } from 'utils/locales';
import './theme/rampOverrides';

import '@ramp/components/dist/style.css';
import '@ramp/icons/dist/style.css';
import * as Sentry from '@sentry/react';
import { getAccessToken, isLoggedIn } from 'axios-jwt';
import ReactDOM from 'react-dom/client';
import { getEnvironment } from 'utils/_utils/environment';
import { extractUserFromToken } from 'utils/_utils/session';
import { reloadOnChunkError } from 'utils/chunkReload';
import App from './App';
import { ToastContainer } from './utils/notifications/toast';

reloadOnChunkError();

const env = getEnvironment();

// The MDM enrollment `state` token (see POST /mdm/enroll/vouch) is a secret and
// must never leave the browser via telemetry. Sentry browser tracing captures
// the full page URL (query string included) into pageload transactions,
// navigation breadcrumbs and resource spans, so redact any `state` query param
// on every outgoing event.
const redactStateInUrl = (value?: string | null): string | undefined => {
  if (!value) return value ?? undefined;
  return value.replace(/([?&]state=)[^&#\s"']*/gi, '$1REDACTED');
};

const scrubStateFromEvent = <T extends Sentry.Event>(event: T): T => {
  if (event.request?.url) event.request.url = redactStateInUrl(event.request.url) ?? event.request.url;
  if (typeof event.transaction === 'string') {
    event.transaction = redactStateInUrl(event.transaction) ?? event.transaction;
  }

  event.breadcrumbs?.forEach(crumb => {
    const data = crumb.data as Record<string, unknown> | undefined;
    if (!data) return;
    (['url', 'to', 'from'] as const).forEach(key => {
      if (typeof data[key] === 'string') data[key] = redactStateInUrl(data[key] as string);
    });
  });

  event.spans?.forEach(span => {
    if (span.description) span.description = redactStateInUrl(span.description) ?? span.description;
    const data = span.data as Record<string, unknown> | undefined;
    if (!data) return;
    Object.keys(data).forEach(key => {
      const val = data[key];
      if (typeof val === 'string' && val.includes('state=')) data[key] = redactStateInUrl(val);
    });
  });

  return event;
};

Sentry.init({
  dsn: 'https://3c143cf13cb5420bb5532eb180dfeb8b@glitchtip.redamp.io/6',

  environment: env,

  beforeSend: scrubStateFromEvent,
  beforeSendTransaction: scrubStateFromEvent,

  initialScope: {
    user: isLoggedIn()
      ? {
          id: extractUserFromToken(getAccessToken())?.user_id || undefined,
          email: extractUserFromToken(getAccessToken())?.email || undefined
        }
      : undefined
  },

  integrations: [Sentry.browserTracingIntegration()],

  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', 'glitchtip.redamp.io']
});

// Render only after the active locale chunk is loaded. Not a top-level `await`: with it, rolldown splits the entry
// into many more files and the first load gets slower.
i18nReady.then(() =>
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <>
      <App />
      <ToastContainer />
    </>
  )
);
